import { BulkUpdateBenefitErrorDto } from 'modules/benefits/dtos'
import { PaymentTypeEnum, type PrimaryPaymentMethod } from 'types/benefit'

type PaymentMethod = PrimaryPaymentMethod | ''

/** CSV columns that must be present in every upload. */
export const REQUIRED_CSV_HEADERS = [
  'benefitId',
  'carrierApplicationPrimaryPaymentMethod',
  'carrierApplicationPrimaryACHAccountNumber',
] as const

export interface ParsedRow {
  key: string
  benefitId: string
  carrierApplicationPrimaryPaymentMethod: PaymentMethod
  carrierApplicationPrimaryACHAccountNumber: string
}

const PAYMENT_METHODS = [
  PaymentTypeEnum.ACH,
  PaymentTypeEnum.VCN,
  PaymentTypeEnum.GROUP_ACH,
  PaymentTypeEnum.CHECK,
] as const

/**
 * Canonicalizes the payment method casing (e.g. `ach` -> `ACH`) so case-insensitive
 * CSV input is accepted. Unrecognized values become '' so the preview flags them
 * rather than silently dropping them to an empty payload at submit time.
 */
export const normalizePaymentMethod = (value: string): PaymentMethod => {
  const trimmed = value.trim()
  return (
    PAYMENT_METHODS.find((m) => m.toLowerCase() === trimmed.toLowerCase()) ?? ''
  )
}

export const getRowErrors = (row: ParsedRow): string[] => {
  const errors: string[] = []
  if (!row.benefitId.trim()) errors.push('Benefit ID is required')
  if (!row.carrierApplicationPrimaryPaymentMethod) {
    errors.push('Payment method is required (ACH, VCN, CHECK, or GROUP ACH)')
  }
  if (
    row.carrierApplicationPrimaryPaymentMethod === PaymentTypeEnum.ACH &&
    !row.carrierApplicationPrimaryACHAccountNumber.trim()
  ) {
    errors.push('ACH account number required')
  }
  if (
    row.carrierApplicationPrimaryPaymentMethod === PaymentTypeEnum.GROUP_ACH &&
    row.carrierApplicationPrimaryACHAccountNumber.trim()
  ) {
    errors.push('GROUP ACH does not require an account number')
  }
  return errors
}

export const isValidRow = (row: ParsedRow): boolean =>
  getRowErrors(row).length === 0

/**
 * Returns the set of benefitIds (trimmed, non-empty) that appear in more than
 * one row. The backend does NOT deduplicate batch items and writes them
 * concurrently, so two rows with the same benefitId race — the final persisted
 * state is nondeterministic and payment rows emit duplicate audit events.
 * Callers surface these and skip them rather than submit both.
 */
export const findDuplicateBenefitIds = (
  rows: readonly ParsedRow[]
): Set<string> => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const row of rows) {
    const id = row.benefitId.trim()
    if (!id) continue
    if (seen.has(id)) {
      duplicates.add(id)
    } else {
      seen.add(id)
    }
  }
  return duplicates
}

/**
 * Validates that the parsed CSV header row contains every required column.
 * Papa.parse only reports structural errors, so a file with the wrong column
 * names parses "successfully" but yields all-empty rows — this surfaces the
 * real cause instead of a wall of per-row "required" errors.
 */
export const findMissingHeaders = (
  parsedHeaders: readonly string[],
  requiredHeaders: readonly string[]
): string[] => {
  const present = new Set(parsedHeaders.map((h) => h.trim()))
  return requiredHeaders.filter((h) => !present.has(h))
}

interface ApiBatchErrorResponse {
  response?: {
    data?: { errors?: BulkUpdateBenefitErrorDto[]; message?: unknown }
  }
  message?: unknown
}

/**
 * Builds the per-row errors for a failed chunk. Prefers the structured
 * per-benefit `errors[]` from the API; only falls back to marking the whole
 * chunk when the failure is opaque (network / 5xx) and per-row status is unknown.
 */
export const mapChunkError = (
  chunk: readonly { benefitId: string }[],
  error: unknown
): BulkUpdateBenefitErrorDto[] => {
  const err = error as ApiBatchErrorResponse
  const structured = err?.response?.data?.errors
  if (Array.isArray(structured) && structured.length > 0) {
    return structured
  }
  const raw =
    err?.response?.data?.message ?? err?.message ?? 'Batch request failed'
  const message = typeof raw === 'string' ? raw : JSON.stringify(raw)
  return chunk.map((item) => ({ benefitId: item.benefitId, message }))
}
